import { useEffect, useState } from 'react'
import { Chip, Spinner, Table } from '@heroui/react'
import { api } from '../lib/api'
import { latestRead } from '../lib/latestRead'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey } from '../lib/staffText'
import type { Branch } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { BREAK_KINDS, breakLines, breakRows, breakTotals, employeesOf, filterLines, newestFirst, toCsv, type BreakKind, type BreakLine } from './breaksCalc'
import { duration, group, isoToday, listSheet, periodRange, type PeriodId } from './insightCalc'
import { DurText, NoData, Tile, type MenuItem } from './insightVisuals'
import { Notice, Pick } from './kit'
import { ExportMenu, PeriodMenu } from './ReportBar'
import { download, xlsx } from './xlsx'

const KIND_KEY: Record<BreakKind, StaffKey> = { lunch: 'reports.lunch', short_break: 'reports.shortBreak', task: 'reports.task' }
// All branches by default only while that is a handful of summary reads.
const ALL_BY_DEFAULT = 12
const AT_ONCE = 4

/** Each branch's summary, a few at a time, as break lines. */
async function readBreaks(targets: Branch[], from: string, to: string, signal: AbortSignal): Promise<BreakLine[]> {
  const out: BreakLine[][] = []
  let next = 0
  const worker = async () => {
    while (next < targets.length) {
      const i = next++
      const b = targets[i]!
      const r = await api.report(b.id, from, to, '', signal)
      out[i] = breakLines(r.breaks ?? [], b)
    }
  }
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, targets.length) }, worker))
  return newestFirst(out.flat())
}

/** Every lunch, short break and task that ran at one branch or all of them (the branch summary's `breaks`). */
export function Breaks({ branches }: { branches: Branch[] }) {
  const { lang, say } = useStaffText()
  const today = isoToday()
  const many = branches.length > 1
  const [picked, setPicked] = useState<string | null>(null)
  const [period, setPeriod] = useState<PeriodId>('week')
  const [custom, setCustom] = useState<[string, string]>([today, today])
  const [employee, setEmployee] = useState('')
  const [kind, setKind] = useState<BreakKind | ''>('')
  const [lines, setLines] = useState<BreakLine[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const shown = picked ?? (many && branches.length <= ALL_BY_DEFAULT ? '' : (branches[0]?.id ?? ''))
  const targets = shown ? branches.filter((b) => b.id === shown) : branches
  const [from, to] = periodRange(period, today, custom)
  const badDates = period === 'custom' && (!from || !to || from > to)
  const targetKey = targets.map((b) => b.id).join(',')

  useEffect(() => {
    if (!targets.length || badDates) return setBusy(false)
    setBusy(true)
    setError('')
    return latestRead((signal) => readBreaks(targets, from, to, signal), {
      done: setLines,
      failed: (err) => {
        setError(explain(err, lang, 'common.unreachable'))
        setLines(null)
      },
      settled: () => setBusy(false),
    })
  }, [targetKey, from, to, badDates]) // eslint-disable-line react-hooks/exhaustive-deps

  const all = lines ?? []
  const people = employeesOf(all)
  const who = people.includes(employee) ? employee : ''
  const rows = filterLines(all, { employee: who, kind })
  const totals = breakTotals(rows)
  const withBranch = shown === ''
  const kindName = (k: BreakKind) => say(KIND_KEY[k])
  const moment = (iso: string, zone?: string, time = false) => formatMoment(iso, lang, time ? { time: true, zone } : { date: true, time: true, zone })
  const sameDay = (a: string, b: string, zone?: string) => formatMoment(a, lang, { date: true, zone }) === formatMoment(b, lang, { date: true, zone })

  const heads = [
    say('common.branch'),
    say('pivot.breaks.employee'),
    say('pivot.breaks.counter'),
    say('pivot.breaks.kind'),
    say('pivot.breaks.reason'),
    say('pivot.breaks.start'),
    say('pivot.breaks.end'),
    `${say('pivot.breaks.time')} (${say('insight.minutes')})`,
    say('pivot.breaks.decidedBy'),
  ]
  const fileName = (ext: string) => {
    const where = withBranch ? say('pivot.allBranches') : (targets[0]?.name ?? '')
    return `${where} - ${say('pivot.bi.breaks')} - ${from === to ? from : `${from} ${to}`}`.replace(/[\\/:*?"<>|]/g, ' ') + ext
  }
  const exportXlsx = () => {
    const title = say('pivot.bi.breaks')
    const about = `${withBranch ? say('pivot.allBranches') : (targets[0]?.name ?? '')}; ${from} – ${to}`
    const sheet = listSheet(title, about, heads, breakRows(rows, kindName, say('reports.stateAuto'), say('pivot.breaks.branchOnly').toLowerCase()), ['text', 'text', 'text', 'text', 'text', 'text', 'text', 'min', 'text'])
    download(xlsx(sheet), fileName('.xlsx'))
  }
  const exportCsv = () => download(new TextEncoder().encode(toCsv([heads, ...breakRows(rows, kindName, say('reports.stateAuto'), say('pivot.breaks.branchOnly').toLowerCase())])), fileName('.csv'), 'text/csv;charset=utf-8')
  const ready = !!lines && !busy && rows.length > 0
  const menu: MenuItem[] = [
    { label: say('pivot.excel'), run: exportXlsx, disabled: !ready },
    { label: say('pivot.csv'), run: exportCsv, disabled: !ready },
  ]

  const tiles: [string, number | null][] = [
    [say('pivot.breaks.count'), null],
    [say('pivot.breaks.total'), totals.seconds],
    ...BREAK_KINDS.map((k): [string, number] => [kindName(k), totals.byKind[k]]),
  ]

  return (
    <div className="insight">
      <div className="insight-top">
        <div className="insight-top__row">
          <Pick
            label={say('common.branch')}
            caption={say('common.branch')}
            value={shown}
            onChange={setPicked}
            options={[...(many ? [{ value: '', label: say('pivot.allBranches') }] : []), ...branches.map((b) => ({ value: b.id, label: b.name }))]}
          />
          <PeriodMenu period={period} onPeriod={setPeriod} custom={custom} onCustom={setCustom} today={today} bad={badDates} />
          <span className="insight-spacer" />
          <div className="insight-actions">
            {busy && (
              <span className="insight-busy">
                <Spinner size="sm" />
                {say('common.reading')}
              </span>
            )}
            <ExportMenu
              items={[
                { label: say('pivot.excel'), icon: 'table', run: ready ? exportXlsx : undefined },
                { label: say('pivot.csv'), icon: 'document', run: ready ? exportCsv : undefined },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="insight-body">
        <main className="insight-main">
          {badDates ? (
            <p className="insight-problem" role="alert">
              {say('insight.badDates')}
            </p>
          ) : error ? (
            <Notice>{error}</Notice>
          ) : (
            lines &&
            (all.length === 0 ? (
              <p className="insight-nodata">{say('pivot.breaks.none')}</p>
            ) : (
              <>
                <div className="insight-kpis insight-kpis--5">
                  {tiles.map(([label, sec]) => (
                    <div key={label} className="insight-kpi">
                      <span className="insight-kpi__label">{label}</span>
                      <strong className="insight-kpi__value">{sec === null ? group(totals.count) : <DurText sec={sec} lang={lang} />}</strong>
                    </div>
                  ))}
                </div>

                <div className="insight-canvas">
                  <Tile
                    id="brrows"
                    title={say('pivot.bi.breaks')}
                    focused={false}
                    onBack={() => {}}
                    menu={menu}
                    tools={
                      <>
                        <Pick
                          label={say('pivot.breaks.employee')}
                          caption={say('pivot.breaks.employee')}
                          value={who}
                          onChange={setEmployee}
                          options={[{ value: '', label: say('pivot.breaks.allEmployees') }, ...people.map((p) => ({ value: p, label: p }))]}
                        />
                        <Pick
                          label={say('pivot.breaks.kind')}
                          caption={say('pivot.breaks.kind')}
                          value={kind}
                          onChange={(v) => setKind(v as BreakKind | '')}
                          options={[{ value: '', label: say('pivot.breaks.allKinds') }, ...BREAK_KINDS.map((k) => ({ value: k, label: kindName(k) }))]}
                        />
                      </>
                    }
                  >
                    {rows.length === 0 ? (
                      <NoData />
                    ) : (
                      <Table variant="secondary" className="insight-grid">
                        <Table.ScrollContainer>
                          <Table.Content aria-label={say('pivot.bi.breaks')}>
                            <Table.Header>
                              {withBranch ? <Table.Column id="branch">{say('common.branch')}</Table.Column> : null}
                              <Table.Column id="employee" isRowHeader>
                                {say('pivot.breaks.employee')}
                              </Table.Column>
                              <Table.Column id="counter">{say('pivot.breaks.counter')}</Table.Column>
                              <Table.Column id="kind">{say('pivot.breaks.kind')}</Table.Column>
                              <Table.Column id="reason">{say('pivot.breaks.reason')}</Table.Column>
                              <Table.Column id="start">{say('pivot.breaks.start')}</Table.Column>
                              <Table.Column id="end">{say('pivot.breaks.end')}</Table.Column>
                              <Table.Column id="time" className="insight-grid__num">
                                {say('pivot.breaks.time')}
                              </Table.Column>
                            </Table.Header>
                            <Table.Body>
                              {rows.map((l, i) => {
                                const id = `${l.branchId}|${l.start}|${l.employee}|${i}`
                                return (
                                  <Table.Row key={id} id={id}>
                                    {withBranch ? <Table.Cell>{l.branch}</Table.Cell> : null}
                                    <Table.Cell>{l.employee || '—'}</Table.Cell>
                                    <Table.Cell>{l.counter || '—'}</Table.Cell>
                                    <Table.Cell>{kindName(l.kind)}</Table.Cell>
                                    <Table.Cell>
                                      {l.reason || '—'}
                                      {l.branchOnly && (
                                        <Chip size="sm" variant="secondary" className="insight-chip">
                                          {say('pivot.breaks.branchOnly')}
                                        </Chip>
                                      )}
                                    </Table.Cell>
                                    <Table.Cell>{moment(l.start, l.zone)}</Table.Cell>
                                    <Table.Cell>{l.end ? moment(l.end, l.zone, sameDay(l.start, l.end, l.zone)) : <span className="insight-muted">{say('pivot.breaks.running')}</span>}</Table.Cell>
                                    <Table.Cell className="insight-grid__num">{duration(l.seconds, lang)}</Table.Cell>
                                  </Table.Row>
                                )
                              })}
                            </Table.Body>
                          </Table.Content>
                        </Table.ScrollContainer>
                      </Table>
                    )}
                  </Tile>
                </div>
              </>
            ))
          )}
        </main>
      </div>
    </div>
  )
}
